import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LayoutGrid, Sparkles, LayoutList, Info } from "lucide-react";
import { KPICard } from "./KPICard";
import { SectionHeader } from "./HelperText";
import EmailAngleTable from "./EmailAngleTable";
import EmailCreativeCatalogTable from "./EmailCreativeCatalogTable";
import EmailAssetTable from "./EmailAssetTable";
import EmailCreativeDetail from "./EmailCreativeDetail";
import EmailAssetDetail from "./EmailAssetDetail";
import { useEmailCreativeAnalyticsData } from "../../hooks/useEmailCreativeAnalyticsData";
import { getEmailMarketingAngles, getEmailMarketingCampaigns } from "../../lib/emailMarketingApi";

const ASSET_FAMILIES = [
  { value: "SUB", label: "Subject" },
  { value: "PRV", label: "Preview Text" },
  { value: "HOK", label: "Hook" },
  { value: "EML", label: "Email Body" },
  { value: "OFF", label: "Offer" },
  { value: "CTA", label: "Call to Action" },
  { value: "PS", label: "P.S." },
  { value: "LPG", label: "Landing Page" },
];

const SUBVIEWS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  { id: "angles", label: "Angles", icon: Sparkles },
  { id: "creatives", label: "Creatives", icon: LayoutList },
  { id: "assets", label: "Assets", icon: LayoutGrid },
];

// DASH-EMAIL-012A Frozen Decision 22: this surface is now backed by the
// real governed Email Marketing catalog (EMAIL-CATALOG-007A), not mock
// data — the banner no longer claims otherwise. It still exists (renamed
// from MockDataBanner) because a genuinely separate, still-true disclosure
// remains: Channel-2 outcome metrics (clicks, quote starts, revenue, etc.)
// are real-catalog-identity but NOT yet measurable — no session-attribution
// runtime exists yet. Catalog identity/composition (real) and analytics
// metrics (not yet available) are deliberately called out as two different
// facts here, so real catalog counts are never mistaken for "everything on
// this page is now fully live."
function CatalogSourceBanner() {
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-indigo-500/20 bg-indigo-500/5 px-3 py-2 text-xs text-indigo-200"
      data-testid="creative-analytics-catalog-banner"
    >
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-indigo-400" />
      <span>
        Catalog identity and composition (Programs, Campaigns, Angles, Creatives, Versions, Fingerprints, Broadcasts) reflect
        the real governed Email Marketing catalog. Channel-2 outcome metrics (clicks, quote starts, revenue, etc.) are not yet
        available — no session-attribution runtime exists yet.
      </span>
    </div>
  );
}

function CreativeAnalyticsSubNav({ subview, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="creative-analytics-subnav">
      {SUBVIEWS.map((sv) => {
        const Icon = sv.icon;
        const isActive = subview === sv.id;
        return (
          <Button
            key={sv.id}
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            className={
              isActive
                ? "gap-1.5"
                : "gap-1.5 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-100"
            }
            onClick={() => onChange(sv.id)}
            data-testid={`creative-analytics-subnav-${sv.id}`}
            aria-current={isActive ? "page" : undefined}
          >
            <Icon className="h-3.5 w-3.5" />
            {sv.label}
          </Button>
        );
      })}
    </div>
  );
}

function CoverageSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="creative-analytics-skeleton">
      {[0, 1, 2, 3, 4].map((i) => (
        <Card key={i} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function NotFoundCard({ label }) {
  return (
    <Card className="glass border-white/10" data-testid="creative-analytics-detail-not-found">
      <CardContent className="p-8 text-center text-slate-400">
        <p className="text-lg">{label} not found</p>
        <p className="text-sm mt-2">It may not exist, or may belong to a different tenant.</p>
      </CardContent>
    </Card>
  );
}

// DASH-EMAIL-012A Frozen Decision 5/8: the real Overview/Angles/Assets
// projections accept no Program/Campaign/Angle filter params at all
// (verified directly against email_marketing_catalog.py's route
// signatures) — only the Creatives subview's endpoint does. Hiding the
// shared filter bar's Program/Campaign/Angle controls per-subview would
// require lifting this view's own local `subview` state up into
// EmailDashboardPanel/EmailFilterBar, which EmailCreativeView's own design
// deliberately keeps local (see this file's own header comment below) —
// a bigger architectural change than this checkpoint's mandate. Disclosing
// the scope honestly here, instead, is the smallest change that avoids
// "a filter silently appears global when it doesn't affect this subview."
function ScopeNotice({ children }) {
  return (
    <p className="text-xs text-slate-500" data-testid="creative-analytics-scope-notice">
      {children}
    </p>
  );
}

function OverviewSubview({ overview }) {
  if (!overview) return null;
  const isUnavailable = overview.channel2_metrics?.availability === "UNAVAILABLE";

  return (
    <div className="space-y-6" data-testid="creative-analytics-overview-subview">
      <div>
        <SectionHeader
          title="Governed Catalog Coverage"
          helper="Counts reflect the real governed Email Marketing catalog, not scoped by the Program/Campaign/Angle filters above."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="creative-analytics-coverage-grid">
          <KPICard title="Angles" value={overview.angle_count} format="number" icon={Sparkles} />
          <KPICard title="Creatives" value={overview.creative_count} format="number" icon={LayoutList} />
          <KPICard title="Creative Versions" value={overview.version_count} format="number" icon={LayoutList} />
          <KPICard title="Broadcasts" value={overview.broadcast_count} format="number" icon={Sparkles} />
          <KPICard
            title="Channel-2 Outcomes"
            value={isUnavailable ? null : overview.channel2_metrics?.value}
            format="number"
            icon={LayoutGrid}
            availability={overview.channel2_metrics?.availability}
            availabilityReason={overview.channel2_metrics?.reason}
          />
        </div>
      </div>
    </div>
  );
}

function AnglesSubview({ angles, onSelectAngle }) {
  return (
    <div className="space-y-4" data-testid="creative-analytics-angles-subview">
      <SectionHeader
        title="Angles"
        helper="Observational — Creative counts are associated with each Angle, a correlation only. Membership uses each Creative's own Angle, not its Campaign's Angle."
      />
      <ScopeNotice>Not scoped by the Program/Campaign/Angle filters above — the real Angle rollup has no such filter yet.</ScopeNotice>
      <EmailAngleTable rows={angles} onSelectAngle={onSelectAngle} />
    </div>
  );
}

// DASH-EMAIL-011b: branches into full Creative detail (EmailCreativeDetail)
// once a row is selected, superseding 011a's list-only surface. Filter
// state stays untouched by this — `selectedCreativeId` is local drill-down
// state, not routed through useEmailFilterState (DASH-EMAIL-011 §18).
function CreativesSubview({
  creatives,
  selectedCreativeId,
  creativeDetail,
  creativeDetailNotFound,
  initialExpandedVersion,
  angleOverride,
  angleLookup,
  onClearAngleOverride,
  onSelectCreative,
  onBackFromDetail,
  onNavigateToAsset,
}) {
  if (selectedCreativeId) {
    return (
      <div data-testid="creative-analytics-creatives-subview">
        {creativeDetailNotFound ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-slate-300 hover:text-slate-100"
              onClick={onBackFromDetail}
              data-testid="email-creative-detail-back"
            >
              Back to Creatives
            </Button>
            <NotFoundCard label="Creative" />
          </>
        ) : (
          <EmailCreativeDetail
            detail={creativeDetail}
            angleLookup={angleLookup.byId}
            campaignLookup={angleLookup.campaignById}
            initialExpandedVersion={initialExpandedVersion}
            onBack={onBackFromDetail}
            onNavigateToAsset={onNavigateToAsset}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="creative-analytics-creatives-subview">
      <SectionHeader
        title="Creatives"
        helper="Observational — each row aggregates across every Version of that Creative (see the version badge). Click a row to open its full Version history."
      />
      {angleOverride && (
        <div
          className="flex items-center justify-between rounded-md border border-indigo-400/30 bg-indigo-500/10 px-3 py-2 text-xs text-indigo-200"
          data-testid="creative-analytics-angle-override-banner"
        >
          <span>
            Filtered by Angle: <span className="font-medium">{angleLookup.byId[angleOverride] || angleOverride}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-indigo-200 hover:text-indigo-100"
            onClick={onClearAngleOverride}
            data-testid="creative-analytics-angle-override-clear"
          >
            Clear
          </Button>
        </div>
      )}
      <EmailCreativeCatalogTable
        rows={creatives}
        angleLookup={angleLookup.byId}
        campaignLookup={angleLookup.campaignById}
        onSelectCreative={onSelectCreative}
      />
    </div>
  );
}

// DASH-EMAIL-011b: branches into full Asset detail (EmailAssetDetail) once
// a row is selected, superseding 011a's inline expand preview.
function AssetsSubview({ assets, family, onFamilyChange, selectedAsset, assetDetail, assetDetailNotFound, onSelectAsset, onBackFromDetail, onNavigateToCreativeVersion }) {
  if (selectedAsset) {
    return (
      <div data-testid="creative-analytics-assets-subview">
        {assetDetailNotFound ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-slate-300 hover:text-slate-100"
              onClick={onBackFromDetail}
              data-testid="email-asset-detail-back"
            >
              Back to Assets
            </Button>
            <NotFoundCard label="Asset version" />
          </>
        ) : (
          <EmailAssetDetail detail={assetDetail} onBack={onBackFromDetail} onNavigateToCreativeVersion={onNavigateToCreativeVersion} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="creative-analytics-assets-subview">
      <SectionHeader
        title="Assets"
        helper="Observational — usage/outcome counts are derived from actual Creative Fingerprint membership. Status is each asset family's own native vocabulary, never normalized across families. Click a row to open its full usage detail."
      >
        <Select value={family} onValueChange={onFamilyChange}>
          <SelectTrigger className="w-[180px] bg-white/5 text-slate-100 border-white/10" data-testid="creative-analytics-asset-family-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASSET_FAMILIES.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SectionHeader>
      <ScopeNotice>Not scoped by the Program/Campaign/Angle filters above — the real Asset rollup has no such filter yet.</ScopeNotice>
      <EmailAssetTable rows={assets} family={family} onSelectAsset={onSelectAsset} />
    </div>
  );
}

/**
 * EmailCreativeView — DASH-EMAIL-011a/b, cut over to the real Email
 * Marketing read API in DASH-EMAIL-012A.
 *
 * Internal sub-navigation (Overview/Angles/Creatives/Assets) is local
 * state, owned here — Creative Analytics remains ONE entry in
 * EMAIL_VIEWS/EmailViewSelector. Because filter state (Period/Program/
 * Campaign/Angle) is owned by the shared useEmailFilterState instance in
 * EmailDashboardPanel, switching subviews here never touches it.
 *
 * `environment` is no longer accepted at all (DASH-EMAIL-012A Frozen
 * Decision 8) — the real Broadcast catalog has no such field, and
 * EMAIL_FILTER_BAR_FIELDS.creativeAnalytics no longer renders that control
 * for this view (see EmailFilterBar.jsx).
 *
 * Performance <-> Creative Analytics cross-navigation is removed entirely
 * (DASH-EMAIL-012A Frozen Decision 17, a deliberate temporary regression):
 * Performance still runs on its own independent synthetic Broadcast
 * catalog (`emailPerformanceMockAdapter.js`'s "brd-*" ids), while this view
 * now uses real "BRC####" identities — the previous reconciliation map was
 * mock<->mock scaffolding that would become a fabricated real<->mock
 * identity claim if reused here. `navigationTarget`/
 * `onNavigateToPerformanceBroadcast` are gone with it. Asset detail ->
 * Creative Version navigation (`initialExpandedVersion`) is UNCHANGED —
 * that is internal Creative Analytics navigation, not a Performance
 * cross-navigation, and was never part of this removal.
 *
 * Reference lookups (Angle/Campaign names) are fetched once here, directly
 * against the real API, independent of the per-subview projection fetch
 * owned by useEmailCreativeAnalyticsData — see that hook's own header
 * comment for why the two are deliberately separate.
 */
export default function EmailCreativeView({ period, program, campaign, angle }) {
  const [subview, setSubview] = useState("overview");
  const [assetFamily, setAssetFamily] = useState("SUB");
  const [selectedCreativeId, setSelectedCreativeId] = useState(null);
  const [initialExpandedVersion, setInitialExpandedVersion] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  // DASH-EMAIL-011b: Angle -> Creatives navigation. A LOCAL override on top
  // of the applied `angle` filter prop, not a mutation of it — clicking an
  // Angle's "View Creatives" affordance should not silently change the
  // Filter bar's own Angle dropdown out from under the operator without an
  // explicit Apply.
  const [angleOverride, setAngleOverride] = useState(null);
  const effectiveAngle = angleOverride || angle;

  const [angleLookup, setAngleLookup] = useState({ byId: {}, campaignById: {} });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [angles, campaigns] = await Promise.all([getEmailMarketingAngles(), getEmailMarketingCampaigns()]);
        if (cancelled) return;
        setAngleLookup({
          byId: Object.fromEntries(angles.map((a) => [a.angle_id, a.name])),
          campaignById: Object.fromEntries(campaigns.map((c) => [c.campaign_id, c.name])),
        });
      } catch {
        // Reference lookups are non-critical for identity resolution — a
        // failed fetch just leaves the maps empty, so every consumer's own
        // `lookup?.[id] || id` fallback renders the raw governed id instead.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { loading, error, overview, angles, creatives, assets, creativeDetail, assetDetail } = useEmailCreativeAnalyticsData({
    subview,
    period,
    program,
    campaign,
    angle: effectiveAngle,
    assetFamily,
    selectedCreativeId,
    selectedAssetFamily: selectedAsset?.family || null,
    selectedAssetId: selectedAsset?.id || null,
    selectedAssetVersion: selectedAsset?.version || null,
  });

  const creativeDetailNotFound = !loading && !error && !!selectedCreativeId && !creativeDetail;
  const assetDetailNotFound = !loading && !error && !!selectedAsset && !assetDetail;

  const handleSubviewChange = (id) => {
    setSubview(id);
    setSelectedCreativeId(null);
    setInitialExpandedVersion(null);
    setSelectedAsset(null);
    setAngleOverride(null);
  };

  const handleSelectCreative = (creativeId) => {
    setSelectedCreativeId(creativeId);
    setInitialExpandedVersion(null);
  };

  const handleSelectAngle = (angleId) => {
    setSubview("creatives");
    setAngleOverride(angleId);
    setSelectedCreativeId(null);
  };

  const handleSelectAsset = (family, id, version) => {
    setSelectedAsset({ family, id, version });
  };

  // Asset Fingerprint slot -> Asset detail.
  const handleNavigateToAsset = (family, id, version) => {
    setSubview("assets");
    setAssetFamily(family);
    setSelectedAsset({ family, id, version });
    setSelectedCreativeId(null);
  };

  // Asset detail's usage table -> the exact Creative Version using it.
  const handleNavigateToCreativeVersionFromAsset = (creativeId, version) => {
    setSubview("creatives");
    setSelectedCreativeId(creativeId);
    setInitialExpandedVersion(version);
    setSelectedAsset(null);
  };

  return (
    <div className="space-y-6" data-testid="email-creative-view">
      <CatalogSourceBanner />
      <CreativeAnalyticsSubNav subview={subview} onChange={handleSubviewChange} />

      {error && (
        <Card className="glass border-red-500/20" data-testid="creative-analytics-error">
          <CardContent className="p-4 text-sm text-red-300">Failed to load Creative Analytics data: {error}</CardContent>
        </Card>
      )}

      {!error && loading && <CoverageSkeleton />}

      {!error && !loading && subview === "overview" && (overview ? (
        <OverviewSubview overview={overview} />
      ) : (
        <Card className="glass border-white/10" data-testid="creative-analytics-empty">
          <CardContent className="p-8 text-center text-slate-400">
            <p className="text-lg">No Creative Analytics data yet</p>
          </CardContent>
        </Card>
      ))}
      {!error && !loading && subview === "angles" && <AnglesSubview angles={angles} onSelectAngle={handleSelectAngle} />}
      {!error && !loading && subview === "creatives" && (
        <CreativesSubview
          creatives={creatives}
          selectedCreativeId={selectedCreativeId}
          creativeDetail={creativeDetail}
          creativeDetailNotFound={creativeDetailNotFound}
          initialExpandedVersion={initialExpandedVersion}
          angleOverride={angleOverride}
          angleLookup={angleLookup}
          onClearAngleOverride={() => setAngleOverride(null)}
          onSelectCreative={handleSelectCreative}
          onBackFromDetail={() => setSelectedCreativeId(null)}
          onNavigateToAsset={handleNavigateToAsset}
        />
      )}
      {!error && !loading && subview === "assets" && (
        <AssetsSubview
          assets={assets}
          family={assetFamily}
          onFamilyChange={setAssetFamily}
          selectedAsset={selectedAsset}
          assetDetail={assetDetail}
          assetDetailNotFound={assetDetailNotFound}
          onSelectAsset={handleSelectAsset}
          onBackFromDetail={() => setSelectedAsset(null)}
          onNavigateToCreativeVersion={handleNavigateToCreativeVersionFromAsset}
        />
      )}
    </div>
  );
}
